import React, { forwardRef, useState } from 'react';
import styled from 'styled-components';
import { FiGithub } from 'react-icons/fi';
import { FaExternalLinkAlt } from 'react-icons/fa';

import { useTheme } from '../contexts/ThemeProvider';
import { workProjects, personalProjects, earlyProjects } from '../data/projects';

// Screenshot carousel, or — for work that can't be shown — a diagram of how it flows.
const Preview = ({ project, reverse }) => {
	const [page, setPage] = useState(0);

	if (project.flow) {
		return (
			<div className={`img-preview flow-preview ${reverse ? 'reverse-img' : ''}`}>
				<div className="flow">
					{project.flow.map((step, index) => (
						<React.Fragment key={index}>
							{index > 0 && <span className="arrow">&darr;</span>}
							{Array.isArray(step) ? (
								<div className="parallel">
									{step.map((branch) => (
										<div className="step" key={branch}>
											{branch}
										</div>
									))}
								</div>
							) : (
								<div className="step">{step}</div>
							)}
						</React.Fragment>
					))}
				</div>
			</div>
		);
	}

	const pages = project.images.length;
	return (
		<div className={`img-preview ${reverse ? 'reverse-img' : ''}`}>
			<div className="imgSlideContainer" style={{ transform: `translateX(${page * -280}%)` }}>
				{[...project.images, project.images[0]].map((image, index) => (
					<img src={image} className="imgSlide" alt={`${project.title} screenshot`} key={index} />
				))}
			</div>
			<div className="buttons">
				<button onClick={() => setPage((page - 1 + pages) % pages)} className="button" aria-label="Previous screenshot">
					&larr;
				</button>
				<button onClick={() => setPage((page + 1) % pages)} className="button" aria-label="Next screenshot">
					&rarr;
				</button>
			</div>
			<div className="dots">
				{project.images.map((image, index) => (
					<button
						key={index}
						className={`dot ${index === page ? 'dot--active' : ''}`}
						onClick={() => setPage(index)}
						aria-label={`Screenshot ${index + 1}`}
					></button>
				))}
			</div>
		</div>
	);
};

const ProjectLinks = ({ project }) => (
	<div className="buttons">
		{project.github && (
			<a href={project.github} className="lessImportantButton" target="_blank" rel="noreferrer" aria-label="GitHub">
				<FiGithub />
			</a>
		)}
		{project.live && (
			<a href={project.live} target="_blank" rel="noreferrer" aria-label="Live site">
				<FaExternalLinkAlt />
			</a>
		)}
		{project.demo && (
			<a href={project.demo} target="_blank" rel="noreferrer">
				take a look
			</a>
		)}
	</div>
);

const FeaturedProject = ({ project, reverse }) => {
	const details = (
		<div className={`details ${reverse ? 'reverse-details' : ''}`}>
			<div className="information">
				<p className="project-label">{project.label}</p>
				<h2>{project.title}</h2>
				<p>{project.description}</p>
				<div className="languages">
					{project.tags.map((tag) => (
						<h4 key={tag}>{tag}</h4>
					))}
				</div>
			</div>
			<ProjectLinks project={project} />
		</div>
	);
	const preview = <Preview project={project} reverse={reverse} />;

	return (
		<div className="container">
			{reverse ? details : preview}
			{reverse ? preview : details}
		</div>
	);
};

const Projects = forwardRef((props, ref) => {
	const color = useTheme();

	return (
		<ProjectContainer color={color.color} ref={ref} id="project">
			<div className="header">
				<h1>my projects</h1>
				<h3>_what i have done so far</h3>
			</div>

			<h1 className="project-header">work_</h1>
			{workProjects.map((project, index) => (
				<FeaturedProject project={project} reverse={index % 2 === 1} key={project.title} />
			))}

			<h1 className="project-header">Featured Projects_</h1>
			{personalProjects.map((project, index) => (
				<FeaturedProject project={project} reverse={index % 2 === 1} key={project.title} />
			))}

			<h1 className="project-header">early work_</h1>
			<div className="early-grid">
				{earlyProjects.map((project) => (
					<div className="project" key={project.title}>
						<div className="details">
							<div className="information">
								<h2>{project.title}</h2>
								<p>{project.description}</p>
								<div className="languages">
									{project.tags.map((tag) => (
										<h4 key={tag}>{tag}</h4>
									))}
								</div>
							</div>
							<ProjectLinks project={project} />
						</div>
					</div>
				))}
			</div>
		</ProjectContainer>
	);
});

export default Projects;

const ProjectContainer = styled.div`
	background-color: ${({ color }) => color.background};
	color: ${({ color }) => color.text};
	width: 100vw;
	padding: 0 8rem;
	margin: 2rem 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 2rem;

	.header {
		align-self: flex-start;
		h1 {
			color: ${({ color }) => color.text};
			text-transform: lowercase;
			font-size: 4rem;
			letter-spacing: 1px;
			font-weight: 800;
		}

		h3 {
			color: ${({ color }) => color.label};
			text-transform: lowercase;
			font-size: 1.5rem;
			letter-spacing: 1.5px;
		}
	}

	.project-header {
		align-self: flex-start;
		font-size: 2.5rem;
		font-weight: 800;
		text-transform: lowercase;
		padding-top: 2rem;
		color: ${({ color }) => color.text};
		letter-spacing: 2px;
	}

	.container {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 3rem;
		padding-bottom: 3rem;

		.img-preview {
			width: 50%;
			height: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
			flex-direction: column;
			border: 2px solid ${({ color }) => color.primary};
			border-radius: 3px;
			padding: 2rem 0;

			&:hover,
			&:active {
				img {
					opacity: 1;
				}
			}

			.imgSlideContainer{
				height: 100%;
				width: 100%;
				display: flex;
				align-items: center;
				justify-content:center;
				transition: transform 1s;
			}

			.imgSlide {
				height: 20rem;
				min-width: 80%;
				height: 100%;
				opacity: 0.6;
				transition: all 0.2s ease-in-out;
				margin: 0 100%;

			}

			.buttons {
				display: flex;
				width: 100%;
				justify-content: space-evenly;
				align-items: center;
				.button {
					z-index: 9;
					border: none;
					background: none;
					font-family: inherit;
					color: ${({ color }) => color.text};
					border-radius: 50%;
					height: 5.5rem;
					width: 5.5rem;
					font-size: 3.25rem;
					cursor: pointer;
				}
			}

			.dots {
				width: 100%;
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 0.5rem;
				.dot {
					border: none;
					background-color: ${({ color }) => color.label};
					opacity: 0.7;
					height: 0.7rem;
					width: 0.7rem;
					border-radius: 50%;
					cursor: pointer;
					transition: all 0.5s;
				}
				.dot--active{
					background-color: ${({ color }) => color.text};
					opacity: 1;
				}
			}
		}

		.flow-preview {
			padding: 3rem 2rem;

			.flow {
				width: 100%;
				display: flex;
				flex-direction: column;
				align-items: center;
				gap: 0.5rem;
				text-transform: lowercase;
				letter-spacing: 2px;

				.step {
					border: 2px solid ${({ color }) => color.primary};
					border-radius: 3px;
					padding: 0.6rem 1.5rem;
					font-size: 1.3rem;
					text-align: center;
					background-color: ${({ color }) => color.background};
					transition: 0.1s ease-in;
					&:hover {
						border-radius: 12px;
						color: ${({ color }) => color.background};
						background-color: ${({ color }) => color.primary};
					}
				}

				.parallel {
					display: flex;
					gap: 1rem;
					flex-wrap: wrap;
					justify-content: center;
				}

				.arrow {
					font-size: 1.5rem;
					color: ${({ color }) => color.label};
				}
			}
		}

		.details {
			width: 50%;
			height: 100%;
			display: flex;
			justify-content: center;
			flex-direction: column;
			gap: 2rem;

			.information {
				display: flex;
				flex-direction: column;
				gap: 1rem;
				width: 100%;
				.project-label {
					font-size: 1.1rem;
					text-transform: lowercase;
					letter-spacing: 2px;
					padding-bottom: 0;
					color: ${({ color }) => color.label};
				}
				h2 {
					font-size: 3rem;
				}

				p {
					font-size: 1.5rem;
					letter-spacing: 2px;
					text-align: justify;
					color: ${({ color }) => color.label};
					padding-bottom: 1rem;
				}

				.languages {
					display: flex;
					flex-wrap: wrap;
					gap: 1rem;
					h4 {
						font-size: 1.2rem;
						border: 2px solid ${({ color }) => color.primary};
						border-radius: 3px;
						padding: 0.2rem 1rem;
						color: ${({ color }) => color.text};
						font-weight: 500;
						text-transform: lowercase;
						letter-spacing: 2px;
					}
				}
			}

			.buttons {
				display: flex;
				gap: 2rem;

				button {
					display: flex;
					align-items: center;
					justify-content: center;
					gap: 1rem;
					color: ${({ color }) => color.background};
					font-size: 1.75rem;
					font-family: 'SHPinscher';
					text-transform: uppercase;
					letter-spacing: 2px;
					font-weight: 600;
					background-color: ${({ color }) => color.primary};
					padding: 0.5rem 1rem;
					border-radius: 3px;
					border: 2px solid ${({ color }) => color.primary};
					letter-spacing: 4.5px;
					transition: 0.1s ease-in;

					&:hover,
					&:active {
						border-radius: 12px;
						transform: translateY(-3px);
						box-shadow: 8px 2px 60px ${({ color }) => color.textShadow};
						background-color: ${({ color }) => color.background};
						border: 2px solid ${({ color }) => color.primary};
						color: ${({ color }) => color.text};
						cursor: pointer;
					}
				}
				a {
					display: flex;
					align-items: center;
					justify-content: center;
					text-decoration: none;
					gap: 1rem;
					color: ${({ color }) => color.background};
					font-size: 1.75rem;
					font-family: 'SHPinscher';
					text-transform: uppercase;
					letter-spacing: 2px;
					font-weight: 600;
					background-color: ${({ color }) => color.primary};
					padding: 0.5rem 1rem;
					border-radius: 3px;
					border: 2px solid ${({ color }) => color.primary};
					letter-spacing: 4.5px;
					transition: 0.1s ease-in;

					&:hover,
					&:active {
						border-radius: 12px;
						transform: translateY(-3px);
						box-shadow: 8px 2px 60px ${({ color }) => color.textShadow};
						background-color: ${({ color }) => color.background};
						border: 2px solid ${({ color }) => color.primary};
						color: ${({ color }) => color.text};
						cursor: pointer;
					}
				}

				.lessImportantButton {
					background-color: ${({ color }) => color.background};
					border: 2px solid ${({ color }) => color.primary};
					color: ${({ color }) => color.text};
					&:hover,
					&:active {
						color: ${({ color }) => color.background};
						background-color: ${({ color }) => color.primary};
						box-shadow: 8px 2px 40px ${({ color }) => color.textShadow};
						cursor: pointer;
					}
				}
			}
		}
	}

	.early-grid {
		width: 100%;
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 1.5rem;
		padding-bottom: 3rem;
		.project {
			height: 100%;
			border: 2px solid ${({ color }) => color.primary};
			padding: 2rem 1.5rem;
			border-radius: 3px;

			.details {
				width: 100%;
				height: 100%;
				display: flex;
				flex-direction: column;
				gap: 2rem;

				.information {
					display: flex;
					flex-direction: column;
					gap: 1rem;
					h2 {
						font-size: 1.5rem;
					}

					p {
						font-size: 1rem;
						letter-spacing: 2px;
						text-align: justify;
						color: ${({ color }) => color.label};
						padding-bottom: 1rem;
					}

					.languages {
						display: flex;
						flex-wrap: wrap;
						gap: 1rem;
						h4 {
							font-size: 1rem;
							border: 2px solid ${({ color }) => color.primary};
							border-radius: 3px;
							padding: 0.1rem 0.8rem;
							color: ${({ color }) => color.text};
							font-weight: 500;
							text-transform: lowercase;
							letter-spacing: 2px;
						}
					}
				}

				.buttons {
					display: flex;
					gap: 1rem;

					button {
						display: flex;
						align-items: center;
						justify-content: center;
						gap: 0.5rem;
						color: ${({ color }) => color.background};
						font-size: 1rem;
						font-family: 'SHPinscher';
						text-transform: uppercase;
						letter-spacing: 2px;
						font-weight: 600;
						background-color: ${({ color }) => color.primary};
						padding: 0.5rem 1rem;
						border-radius: 3px;
						border: 2px solid ${({ color }) => color.primary};
						letter-spacing: 4.5px;
						transition: 0.1s ease-in;

						&:hover,
						&:active {
							border-radius: 12px;
							transform: translateY(-3px);
							box-shadow: 8px 2px 60px ${({ color }) => color.textShadow};
							background-color: ${({ color }) => color.background};
							border: 2px solid ${({ color }) => color.primary};
							color: ${({ color }) => color.text};
							cursor: pointer;
						}
					}
					a {
						display: flex;
						align-items: center;
						justify-content: center;
						gap: 0.5rem;
						color: ${({ color }) => color.background};
						font-size: 1rem;
						font-family: 'SHPinscher';
						text-transform: uppercase;
						letter-spacing: 2px;
						font-weight: 600;
						background-color: ${({ color }) => color.primary};
						padding: 0.5rem 1rem;
						border-radius: 3px;
						border: 2px solid ${({ color }) => color.primary};
						letter-spacing: 4.5px;
						transition: 0.1s ease-in;

						&:hover,
						&:active {
							border-radius: 12px;
							transform: translateY(-3px);
							box-shadow: 8px 2px 60px ${({ color }) => color.textShadow};
							background-color: ${({ color }) => color.background};
							border: 2px solid ${({ color }) => color.primary};
							color: ${({ color }) => color.text};
							cursor: pointer;
						}
					}

					.lessImportantButton {
						background-color: ${({ color }) => color.background};
						border: 2px solid ${({ color }) => color.primary};
						color: ${({ color }) => color.text};
						&:hover,
						&:active {
							color: ${({ color }) => color.background};
							background-color: ${({ color }) => color.primary};
							box-shadow: 8px 2px 40px ${({ color }) => color.textShadow};
							cursor: pointer;
						}
					}
				}
			}
		}

	}

	@media (max-width: 1024px){
		padding: 2rem 3rem;
		margin: 3rem 0;

		.container{
			flex-direction: column;
			gap: 1rem;

			.img-preview{
				width: 100%;
			}

			.details{
				width: 100%;
				gap: 2rem;

				.information{
					gap: 0.5rem
				}

				.buttons{
					gap: 0.5rem
				}
			}

			.reverse-details{
				order: 1;
			}

			.reverse-img{
				order: 0;
			}
		}

		.early-grid{
			grid-template-columns: 1fr 1fr;
		}
	}

	@media (max-width: 768px){
		padding: 0 2rem;
		.early-grid{
			grid-template-columns: 1fr;
		}
		.flow-preview .flow .step{
			font-size: 1.1rem;
			padding: 0.5rem 1rem;
		}
	}
`;
